import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { catchError, Observable } from "rxjs";

import { environment } from "@environment/environment";
import { CoreResponse } from "@models/core-response.interface";
import { ExceptionProvider } from "@providers/exception.provider";
import { RoleMenuPermissionDto } from "@auth/interfaces/role-menu-permission.dto";
import { RoleMenuPermission } from "@auth/models/role-menu-permission.model";
import { Menu } from "@auth/models/menu.model";

/**
 * Service for handling role-menu-permission-related operations.
 */
@Injectable({ providedIn: 'root' })
export class RoleMenuPermissionService {
  private _httpClient = inject(HttpClient);
  private _exceptionProvider = inject(ExceptionProvider);

  private API_PATH = `${environment.apiBaseUrl}/v1/role-menu-permission`;

  /**
   * Finds all role-menu-permissions for a given role.
   * @param roleId The ID of the role.
   * @returns An observable with the core response containing the list of role-menu-permissions.
   */
  findAllByRoleId(roleId: number): Observable<CoreResponse<RoleMenuPermission[]>> {
    return this._httpClient.get<CoreResponse<RoleMenuPermission[]>>(this.API_PATH, { params: { roleId } }).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Adds a permission to a role.
   * @param roleMenuPermissionDto The DTO containing the role, menu, and permission IDs.
   * @returns An observable with the core response containing the new role-menu-permission.
   */
  addPermissionToRole(roleMenuPermissionDto: RoleMenuPermissionDto): Observable<CoreResponse<RoleMenuPermission>> {
    return this._httpClient.post<CoreResponse<RoleMenuPermission>>(this.API_PATH, { ...roleMenuPermissionDto }).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Removes a permission from a role.
   * @param roleMenuPermissionId The ID of the role-menu-permission to remove.
   * @returns An observable with the core response indicating if the permission was removed.
   */
  removePermissionToRole(roleMenuPermissionId: number): Observable<CoreResponse<{ removed: boolean; }>> {
    return this._httpClient.delete<CoreResponse<{ removed: boolean; }>>(`${this.API_PATH}/${roleMenuPermissionId}`).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }

  /**
   * Finds the menu for a given role.
   * @param roleId The ID of the role.
   * @returns An observable with the core response containing the list of menu items.
   */
  findMenuByRoleId(roleId: number): Observable<CoreResponse<Menu[]>> {
    return this._httpClient.get<CoreResponse<Menu[]>>(`${this.API_PATH}/menu`, { params: { roleId } }).pipe(
      catchError((error: HttpErrorResponse) => this._exceptionProvider.handleGenricError(error))
    );
  }
}
